import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../../styles/components/ui/_confirm-dialog.scss";

// ─── Tone config ──────────────────────────────────────────────────────────────
const TONE_META = {
    danger: {
        icon: (
            <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                aria-hidden="true"
            >
                <path
                    d="M11 3L20.5 19H1.5L11 3z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                />
                <path
                    d="M11 9v5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
                <circle cx="11" cy="16.5" r="0.8" fill="currentColor" />
            </svg>
        ),
        iconClass: "cd__icon--danger",
        confirmClass: "cd__btn--danger",
    },
    default: {
        icon: (
            <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                aria-hidden="true"
            >
                <circle
                    cx="11"
                    cy="11"
                    r="8.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                />
                <path
                    d="M11 8v4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />
                <circle cx="11" cy="14.5" r="0.8" fill="currentColor" />
            </svg>
        ),
        iconClass: "cd__icon--default",
        confirmClass: "cd__btn--default",
    },
};

export default function ConfirmDialog({
    open,
    title,
    description,
    confirmText = "Confirm",
    cancelText = "Cancel",
    tone = "default",
    onConfirm,
    onCancel,
}) {
    const cancelRef = useRef(null);
    const confirmRef = useRef(null);

    // Close on Escape, trap focus between the two buttons
    useEffect(() => {
        if (!open) return;
        const prev = document.activeElement;
        confirmRef.current?.focus();

        const onKey = (e) => {
            if (e.key === "Escape") {
                onCancel?.();
                return;
            }
            if (e.key === "Tab") {
                const els = [cancelRef.current, confirmRef.current].filter(
                    Boolean
                );
                if (!els.length) return;
                const first = els[0],
                    last = els[els.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("keydown", onKey);
            prev?.focus?.();
        };
    }, [open, onCancel]);

    if (!open || typeof document === "undefined") return null;

    const meta = TONE_META[tone] ?? TONE_META.default;

    return createPortal(
        <div
            className="cd"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cd-title"
            aria-describedby={description ? "cd-desc" : undefined}
        >
            <div
                className="cd__backdrop"
                onClick={onCancel}
                aria-hidden="true"
            />

            <div className="cd__card">
                {/* Icon tile */}
                <div
                    className={`cd__icon ${meta.iconClass}`}
                    aria-hidden="true"
                >
                    {meta.icon}
                </div>

                {/* Text */}
                <div className="cd__text">
                    <p className="cd__title" id="cd-title">
                        {title}
                    </p>
                    {description && (
                        <p className="cd__desc" id="cd-desc">
                            {description}
                        </p>
                    )}
                </div>

                {/* Actions */}
                <div className="cd__actions">
                    <button
                        ref={cancelRef}
                        type="button"
                        className="cd__btn cd__btn--cancel"
                        onClick={onCancel}
                    >
                        {cancelText}
                    </button>
                    <button
                        ref={confirmRef}
                        type="button"
                        className={`cd__btn ${meta.confirmClass}`}
                        onClick={onConfirm}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
